import { Me, User } from '@frontend/data-access';
import { formatNumber } from '@frontend/shared/util';
import { Progress } from 'antd';
import useTranslation from 'next-translate/useTranslation';

/* eslint-disable-next-line */
export interface UserLevelProps {
  size?: 'small' | 'medium';
  labelType?: 'primary' | 'default' | 'dark';
  font?: 'default' | 'medium';
  data: Me | User;
  onClick?: () => void;
  className?: string;
  strokeColor?: string;
}

export function UserLevel({
  size = 'medium',
  data,
  labelType = 'primary',
  onClick,
  className,
  strokeColor = '#2563eb',
  font = 'default',
}: UserLevelProps) {
  const { t: tcommon } = useTranslation('common');

  const getEndPointText = () => {
    if (!data?.rank?.rank || data?.rank?.rank <= 9999) {
      return `${formatNumber(data?.rank?.badge.endPoint)}`;
    }
    return '9999+';
  };

  const getPercent = () => {
    if (data?.rank?.rank && data.rank.rank > 9999) {
      return 100;
    }
    if (!data?.rank?.rank) {
      return 0;
    }
    return (
      (100 * (data.rank?.rank - data.rank?.badge.startPoint)) /
      (data.rank?.badge.endPoint - data.rank?.badge.startPoint)
    );
  };

  const getLevelText = () => {
    let className = '';
    if (labelType === 'primary') {
      className = 'text-primary-600';
    } else if (labelType === 'dark') {
      className = 'text-gray-1000';
    } else {
      className = 'text-gray-600';
    }

    if (size === 'small') {
      className += ' text-xs';
    } else {
      className += ' text-sm';
    }

    if (onClick) {
      className += ' cursor-pointer';
    }

    return (
      <span className={className} data-test="level-badge" onClick={onClick}>
        {tcommon('level_badge', {
          level: data?.rank?.badge.level,
          badge: data?.rank?.badge.name,
        })}
      </span>
    );
  };

  const getRankText = () => {
    let className = 'text-nowrap text-gray-600';

    if (size === 'small') {
      className += ' text-xs';
    } else {
      className += ' text-sm';
    }

    if (font === 'medium') {
      className += ' font-medium';
    }

    if (onClick) {
      className += ' cursor-pointer';
    }

    return (
      <div className={className}>
        {`${formatNumber(data?.rank?.rank)} / ${getEndPointText()}`}
      </div>
    );
  };

  const getStrokeColor = () => {
    if (getPercent() === 100) {
      // Green color
      return undefined;
    }
    return strokeColor;
  };

  return (
    <div className={className + ' w-full'}>
      <div
        className="flex justify-between gap-2 items-center"
        onClick={onClick}
      >
        {getLevelText()}
        {getRankText()}
      </div>
      <Progress
        percent={getPercent()}
        showInfo={false}
        strokeColor={getStrokeColor()}
      />
    </div>
  );
}

export default UserLevel;
